import React, { useState, useEffect } from 'react'
import { ClientCertificate } from '@/types'
import { Input } from '@/components/ui/Input'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError } from '@/utils/toast'
import { SingleCertificateDisplay } from './SingleCertificateDisplay'

interface ClientCertificateSectionProps {
  initialCertificate?: ClientCertificate
  onSave: (certificate: ClientCertificate) => Promise<void>
  readOnly?: boolean
}

export const ClientCertificateSection: React.FC<ClientCertificateSectionProps> = ({
  initialCertificate,
  onSave,
  readOnly = false,
}) => {
  const [isSaving, setIsSaving] = useState(false)

  /** Editable working state */
  const [clientCertificate, setClientCertificate] = useState<ClientCertificate>(
    initialCertificate || {}
  )

  /** Last successfully submitted certificate */
  const [submittedCertificate, setSubmittedCertificate] = useState<ClientCertificate>(
    initialCertificate || {}
  )

  /** File selected in FileInput */
  const [clientCertificateFile, setClientCertificateFile] = useState<File | null>(null)

  /** Dirty flag */
  const [isDirty, setIsDirty] = useState(false)

  // Create a File object with forced filename for display
  const displayFile = clientCertificateFile
    ? new File([clientCertificateFile], 'client.p12', { type: clientCertificateFile.type })
    : null

  useEffect(() => {
    if (initialCertificate) {
      setClientCertificate(initialCertificate)
      setSubmittedCertificate(initialCertificate)
      setIsDirty(false)
    }
  }, [initialCertificate])

  const markDirty = () => setIsDirty(true)

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const updated = {
      ...clientCertificate,
      password: value,
    }
    setClientCertificate(updated)
    markDirty()
  }

  const handleFileChange = (files: FileList | null) => {
    const file = files?.[0]
    if (file) {
      // Only allow .p12 files
      if (!file.name.toLowerCase().endsWith('.p12')) {
        toastError('Seuls les fichiers .p12 sont autorisés pour le certificat client')
        return
      }

      setClientCertificateFile(file)
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        const updated = {
          ...clientCertificate,
          filename: 'client.p12', // Force filename to client.p12
          data: base64,
        }
        setClientCertificate(updated)
        markDirty()
      }
      reader.onerror = () => {
        toastError('Erreur lors de la lecture du fichier de certificat')
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveFile = (_index: number) => {
    setClientCertificateFile(null)
    const updated = {
      ...clientCertificate,
      filename: undefined,
      data: undefined,
    }
    setClientCertificate(updated)
    markDirty()
  }

  const handleReset = () => {
    setClientCertificate({ ...submittedCertificate })
    setClientCertificateFile(null)
    setIsDirty(false)
  }

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      await onSave(clientCertificate)
      setSubmittedCertificate(clientCertificate)
      setClientCertificateFile(null)
      setIsDirty(false)
    } catch (error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Erreur lors de la sauvegarde du certificat client'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const hasExistingCertificate =
    submittedCertificate?.filename && submittedCertificate?.data && !clientCertificateFile

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
        Certificat client
      </h3>
      <div className="space-y-6">
        {hasExistingCertificate && submittedCertificate && (
          <SingleCertificateDisplay certificate={submittedCertificate} />
        )}

        <FileInput
          label="Fichier certificat client (format .p12 uniquement)"
          accept=".p12"
          onChange={handleFileChange}
          selectedFiles={displayFile ? [displayFile] : []}
          onFileRemove={handleRemoveFile}
          disabled={readOnly}
        />
        <Input
          label="Mot de passe du certificat client"
          type="password"
          name="password"
          value={clientCertificate.password || ''}
          onChange={handlePasswordChange}
          placeholder="Mot de passe"
          disabled={readOnly}
        />
      </div>
      {!readOnly && (
        <div className="flex justify-end gap-2 mt-4">
          {isDirty && (
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              Réinitialiser
            </Button>
          )}
          <Button onClick={handleSubmit} isLoading={isSaving} disabled={!isDirty}>
            Enregistrer le certificat client
          </Button>
        </div>
      )}
    </div>
  )
}
